import { safe } from "@orpc/client";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import z from "zod";
import { useAppForm } from "@/components/form";
import { ComboboxUser } from "@/components/forms/combobox-user";
import { FieldGroup } from "@/components/ui/field";
import {
	FormCard,
	FormCardContent,
	FormCardHeader,
	FormCardTitle,
} from "@/components/ui/form-card";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupText,
	InputGroupTextarea,
} from "@/components/ui/input-group";
import { orpc, validationErrors } from "@/orpc/client";

export const Route = createFileRoute("/_app/publishers/$publisherId/")({
	component: Page,
	params: z.object({
		publisherId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.publishers.show.queryOptions({
				input: {
					publisherId: params.publisherId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { data: publisher } = useSuspenseQuery(
		orpc.publishers.show.queryOptions({
			input: {
				publisherId: params.publisherId,
			},
		}),
	);

	const updateMutation = useMutation(
		orpc.publishers.update.mutationOptions({
			onSuccess: (_data, _payload, _result, context) => {
				context.client.invalidateQueries({ queryKey: orpc.publishers.key() });
				toast.success("Publisher updated successfully.");
			},
			onError: () => {
				toast.error("Failed to update this publisher.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			APIKey: publisher.APIKey,
			Description: publisher.Description || "",
			Owner: publisher.Owner.ID,
			PublisherName: publisher.PublisherName || "",
		},
		canSubmitWhenInvalid: true,
		validators: {
			onSubmitAsync: async ({ value }) => {
				if (!value.Owner) {
					return {
						fields: {
							Owner: !value.Owner ? "Owner is required." : undefined,
						},
					};
				}

				const { error } = await safe(
					updateMutation.mutateAsync({
						publisherId: params.publisherId,
						...value,
						Owner: value.Owner,
					}),
				);

				if (error) {
					return validationErrors(error) ?? "An error occurred while updating the publisher.";
				}
			},
		},
	});

	return (
		<form.AppForm>
			<form
				className="relative"
				id={form.formId}
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<div className="grid grid-cols-12 gap-6 px-4 lg:px-6">
					<FormCard className="col-span-12" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Publisher Info</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="PublisherName"
									children={(field) => (
										<field.FormItem className="lg:col-span-6">
											<field.FormLabel>Publisher Name</field.FormLabel>
											<field.FormInput />
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Owner"
									children={(field) => (
										<field.FormItem className="lg:col-span-6">
											<field.FormLabel>Owner</field.FormLabel>
											<ComboboxUser
												value={field.state.value}
												onChange={field.handleChange}
												onBlur={field.handleBlur}
												isInvalid={field.state.meta.isTouched && !field.state.meta.isValid}
											/>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Description"
									children={(field) => (
										<field.FormItem>
											<field.FormLabel>Description</field.FormLabel>
											<InputGroup>
												<InputGroupTextarea
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													value={field.state.value}
												/>
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value || "".length}/140 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="APIKey"
									children={(field) => (
										<field.FormItem>
											<field.FormLabel>API Key</field.FormLabel>
											<field.FormInput />
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
							<form.FormErrorMessages />
						</FormCardContent>
					</FormCard>
					<form.FormSaveBar />
				</div>
				<form.FormBottomBar />
			</form>
		</form.AppForm>
	);
}
